import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import timeGridPlugin from "@fullcalendar/timegrid";
import { addDays, format, subDays } from "date-fns";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  memo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

interface CalendarInterface {
  resources?: any;
  events?: any;
  setCurrentViewAdmin?: any;
  setCalendarDate?: any;
  startDate?: any;
  calendarDate?: any;
  setStartDateCalendar?: any;
  setEndDateCalendar?: any;
  setStartDateCalendarDay?: any;
  currentViewAdmin?: any;
}


/**
 * Resource Label Component - Employee card with utilization
 */
const ResourceLabelContent = memo(({ arg }: { arg: any }) => {
  const {
    title,
    total_working_hours,
    allocated_working_hours,
    utilization_percentage,
    extendedProps,
  } = arg?.resource;

  const total = total_working_hours ?? extendedProps?.total_working_hours ?? 0;

  const allocated =
    allocated_working_hours ?? extendedProps?.allocated_working_hours ?? 0;

  const utilization =
    utilization_percentage ?? extendedProps?.utilization_percentage ?? 0;

  return (
    <div className="custom-resource-label relative cursor-pointer">
      <div
        className={`max-h-[100%] relative client_card_calender_div self-stretch bg-white  flex flex-nowarp flex-row items-center justify-start py-[2px] px-0 gap-[8px] text-left calleftcard-event-padding text-xs text-light-font font-body-reg border-t-[1px] border-gray-300 mq450:flex-nowrap`}
      >
        <div className="min-w-[40px] h-[40px] d-inline-block no-underline inner-meeting-tooltip-main-calendar">
          <span className="rounded-circle avatar-xxs">
            {title
              ?.split(" ")
              .map((n: string) => n[0])
              .join("")
              .toUpperCase()}
          </span>
        </div>
        <div className="flex-1 flex flex-col items-start justify-center gap-[8px] min-w-[183px] min-h-[72px]">
          <div className="self-stretch block flex-row items-start justify-start text-sm text-dark-font clientcrad-name-blg">
            <div className="flex-1 relative capitalize font-semibold text-sm text-gray-900 dark:text-white">
              {title}
            </div>
          </div>

          <div className="self-stretch block flex-row items-start justify-start text-sm text-dark-font clientcrad-name-blg">
            {/* Hours */}
            <div className="text-xs text-gray-600 mt-1">
              Hours: {allocated}H/ {total}H
            </div>

            {/* Utilization */}
            <div className="text-xs text-gray-600 mt-1">
              Utilization: {utilization}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ResourceLabelContent.displayName = "ResourceLabelContent";

/**
 * Memoized Event Content Component - handles meetings, tasks, and goals
 */
const EventContent = memo(({ eventInfo, onEventClick }: { eventInfo: any; onEventClick: (payload: any) => void }) => {
  const { start, end, title, id } = eventInfo?.event;
  const event =
    eventInfo?.event?.extendedProps?.originalEvent || eventInfo?.event;
  const {
    eventType,
    goal_members,
    project_name,
    meeting_type_name,
    meeting_id,
    host_name,
    goal_name,
    hours,
    mins,
    status_name,
    current_status_name,
    start_time,
    end_time,
    start_date,
    end_date,
    is_email_send
  } = eventInfo?.event?.extendedProps;

  const handleEventClick = useCallback(() => {
    onEventClick({ id, start, end, eventType, event,meeting_id });
  }, [onEventClick, id, start, end, eventType, event,meeting_id]);

  // Get status badge for tasks
  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: string } = {
      "Production Release":"prod-rel-class",
      "Not Started": "notstarted-class",
      "In Progress": "inprogress-class",
      Closed: "closed-class",
      Testing: "testing-class",
    };
    return statusMap[status] || "notstarted-class";
  };

  return (
    <div
      className={`p-2 cursor-pointer rounded border-l-4 ${
        (eventType === "task" || eventType === "goal") &&
        getStatusBadge(status_name || current_status_name)
      } meeting_card_box_dv event-card`}
      onClick={handleEventClick}
      role="button"
      tabIndex={0}
    >
      {/* Task-specific information */}
      {eventType === "task" && (
        <>
          <div className="grid items-start justify-between gap-0">
            <div className="flex-1 min-w-0">
              <p className="font-semibold my-0 text-sm truncate">
                Task : {title}
              </p>
              {goal_name && (
                <p className="text-xs text-gray-600 my-0">Goal: {goal_name}</p>
              )}
              <div>
                {project_name && (
                  <p className="text-xs text-gray-600 my-0">
                    Project: {project_name}
                  </p>
                )}
                <p className={`text-xs my-0  w-[390px] flex `}>
                  Duration : {`${hours} Hours ${mins} Minutes`}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Goal-specific information */}
      {eventType === "goal" && (
        <>
          <div className="grid items-start justify-between gap-0">
            <div className="flex-1 min-w-0">
              <p className="font-semibold my-0 text-sm truncate">
                Goal : {title}
              </p>
              {goal_members && goal_members.length > 0 && (
                <p
                  title={goal_members.map((m: any) => m.member_name).join(", ")}
                  className="text-xs text-gray-600 my-0"
                >
                  Members: {goal_members?.length}
                </p>
              )}
              {project_name && (
                <p className="text-xs text-gray-600 my-0">
                  Project: {project_name}
                </p>
              )}
              <p className={`text-xs my-0  w-[390px] flex `}>
                Date : {`${start_date} - ${end_date}`}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Meeting-specific information */}
      {eventType === "meeting" && (
        <>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 grid min-w-0">
                {
                  is_email_send && <span className="flex items-center gap-1"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" fill="#22C55E" />
                  <path
                    d="M7 12.5L10.2 15.5L17 8.5"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg> <span className="text-sm font-semibold dark:text-white">Completed</span></span>
                }
              <p className="font-semibold my-0 text-sm truncate">
                Meeting : {title}
              </p>
              <p className="text-xs text-gray-600 my-0">
                Type: {meeting_type_name}
              </p>
              <p className={`text-xs my-0  w-[390px] flex `}>
                Time : {`${start_time} - ${end_time}`}
              </p>
              {host_name && (
                <p className="text-xs text-gray-600 my-0 w-[390px]">
                  Host: {host_name}
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
});

EventContent.displayName = "EventContent";

const CalendarAdmin: FunctionComponent<CalendarInterface> = ({
  resources,
  events,
  setCurrentViewAdmin,
  setCalendarDate,
  startDate,
  setEndDateCalendar,
  setStartDateCalendar,
  setStartDateCalendarDay,
  currentViewAdmin,
}) => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const navigate = useNavigate();

    /**
   * Single stable click handler shared by every event card
   */
  const handleEventClick = useCallback(
    ({ id, start, end, eventType, event,meeting_id }: any) => {
      const safeFormat = (d: any) => {
        const date = new Date(d);
        return isNaN(date.getTime())
          ? null
          : format(date, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
      };

      if (eventType === "task") {
        navigate("/task_detail", { state: { taskId: id } });
      } else if (eventType === "goal") {
        navigate("/goal_view", {
          state: { goalID: id, projectName: event?.project_name },
        });
      } else {
        navigate("/meeting_detail", {
          state: {
            meetingID: meeting_id,
            startMeetDate: safeFormat(start),
            endMeetDate: safeFormat(end),
          },
        });
      }
    },
    [navigate],
  );


  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api || !startDate) return;
    const current = format(api.getDate(), "yyyy-MM-dd");
    if (current !== startDate) {
      api.gotoDate(startDate);
    }
  }, [startDate]);

  /**
   * Handle when calendar dates range changes
   */
  const handleDatesSet = useCallback(
    (arg: any) => {
      
      const start = format(arg.view.currentStart, "yyyy-MM-dd");

      const end = format(subDays(arg.view.currentEnd, 1), "yyyy-MM-dd");

      setCurrentViewAdmin(arg.view.type);

      setCalendarDate({
        start,
        end,
      });

      if (arg.view.type === "resourceTimelineDay") {
        setStartDateCalendar(start);
        setEndDateCalendar(start);
        setStartDateCalendarDay(start);
      } else {
        setStartDateCalendar(start);
        setEndDateCalendar(end);
        setStartDateCalendarDay(start);
      }
    },
    [
      setCalendarDate,
      setCurrentViewAdmin,
      setStartDateCalendar,
      setEndDateCalendar,
      setStartDateCalendarDay,
    ],
  );

  /**
   * Render event with memoized component
   */
  const renderEventContent = useCallback(
    (eventInfo: any) => (
      <EventContent eventInfo={eventInfo} onEventClick={handleEventClick} />
    ),
    [handleEventClick],
  );

  /**
   * FullCalendar view configurations
   */
  const viewConfigs = useMemo(
    () => ({
    
      resourceTimelineWeek: {
        slotDuration: { days: 1 }, // Each slot represents a whole day
        slotLabelFormat: function (args: any) {
          const offset = new Date().getTimezoneOffset();
          const isNegative = offset > 0; // JavaScript returns offset in minutes, positive for negative timezones
          let date: any;
          if (isNegative) {
            const data = new Date(args.date.marker); // Ensure it's a Date object
            date = addDays(data, 1);
          } else {
            date = new Date(args.date.marker); // Ensure it's a Date object
          }
          const dayName = date.toLocaleDateString("en-US", {
            weekday: "short",
          }); // 'Sun'
          const dayNumber = date.toLocaleDateString("en-US", {
            day: "numeric",
          }); // '25'
          return `${dayName} ${dayNumber}`; // Combine day name and day number
        },
        slotLabelInterval: { days: 1 }, // Set label interval to match slot duration
      },
      resourceTimelineDay: {
        slotDuration: "02:00:00",
        slotLabelInterval: "02:00",
      },
    }),
    [],
  );

  // Handle window resize for responsive resource area width
  const handleResize = useCallback(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  const renderResourceLabel = useCallback(
  (arg: any) => <ResourceLabelContent arg={arg} />,
  []
);

  return (
    <>
      <div className="w-full">
        <FullCalendar
          ref={calendarRef}
          plugins={[
            resourceTimelinePlugin,
            dayGridPlugin,
            interactionPlugin,
            timeGridPlugin,
          ]}
          headerToolbar={{
            left: "prev,next",
            center: "title",
            right: "resourceTimelineWeek,resourceTimelineDay",
          }}
          initialDate={startDate}
          datesSet={handleDatesSet}
          initialView={currentViewAdmin}
          firstDay={1}
          views={viewConfigs}
          slotMinWidth={60}
          editable={false}
          selectable={false}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={true}
          resourceOrder="title"
          resources={resources}
          events={events}
          resourceAreaWidth={isMobile ? "50px" : "200px"}
          resourceAreaHeaderContent={() => (
            <div className="p-2 font-bold text-sm md:text-base text-gray-900 dark:text-white emp-label-cal-item">
              <span className="inner-emp-label-cal-item">
                {" "}
                {isMobile ? "Emp." : "Employees"}
              </span>
            </div>
          )}
          resourceLabelContent={renderResourceLabel}
          height="auto"
          contentHeight="auto"
          eventContent={renderEventContent}
          windowResize={handleResize}
        />
      </div>
    </>
  );
};

export default CalendarAdmin;
